import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { capitalizeFirstLetter } from '@common/react/utils/utils';
import { Lang } from '@common/typescript/objects/Lang';
import { parseQuery } from '@common/typescript/utils/url';
import {
	defaultTransformFiltersBeforeHandleUrl,
	ItemsProvider,
	useItemsProviderContext,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import ServerPageProvider from '@common/react/components/UI/lang/SeverPageProviderWithLang/ServerpageProviderWithLang';
import LinkWithLang from '@common/react/components/UI/lang/LinkWithLang/LinkWithLang';
import loadable from '@common/react/loadable/loadableSettings';

import { getPageInfo, selectProps } from '@app/components/Utils';
import { Blog as BlogInterface } from '@app/objects/Pages/BlogPage';

import '@app/scss/pages/blog.scss';

const Pagination = loadable(() => import(/* webpackChunkName: "Pagination" */ '@common/react/components/base/Pagination/Pagination'));

interface BlogInnerProps {
	lang: Lang;
}

const BlogInner: React.FC<BlogInnerProps> = ({
	lang,
}) => {
	const { state: { items, pagination }, actions: { handleChange } } = useItemsProviderContext<BlogInterface>();

	const { t, i18n } = useTranslation();
	const langString = capitalizeFirstLetter(i18n.language);

	return <>
		{items && <>
			{items.map((item: BlogInterface, index: number) => {
				const pageInfo = getPageInfo(item, lang);
				return (
					<div className="post" key={item.id}>
						<LinkWithLang to={`/${item[langString === 'En' ? 'fullPath' : 'fullPathEs']}`} className="post">
							<h3 className="post__title">{item[`shortName${langString}`]}</h3>
						</LinkWithLang>
						<div className="external-html" dangerouslySetInnerHTML={{ __html: pageInfo ? pageInfo.shortBody : '' }} />
						<LinkWithLang
							to={`/${item[langString === 'En' ? 'fullPath' : 'fullPathEs']}`}
							className="general-form-button general-form-button_sm"
						>
							{t('site.Read More')}
						</LinkWithLang>
					</div>
				);
			})}
			{pagination.total > 10
				&& <Pagination
					className="dark-pagination"
					current={pagination.current}
					pageSize={pagination.pageSize}
					total={pagination.total ? pagination.total : 0}
					onChange={(page) => handleChange({ page })}
					selectProps={selectProps}
				/>
			}
		</>}
	</>;
};

const Blog: React.FC = () => {
	const { t, i18n } = useTranslation();
	const location = useLocation();
	const lang = i18n.language === 'es' ? Lang.Es : Lang.En;

	const search = parseQuery(location.search);
	const defCurrent = +search.page || 1;

	return (
		<div className="blog-page inner-page container">
			<BreadCrumbs title={t('seo.blogPage.title')} />
			<ServerPageProvider
				path="blog"
				inner={(serverPage) =>
					<ItemsProvider
						items={serverPage?.items?.list}
						pagination={{ current: defCurrent, pageSize: 10, total: serverPage?.items?.count }}
						loadRequest="blogListRemote"
						filters={{ page: defCurrent, withRows: true }}
						withHandleUrl
						pageInSearch
						transformFiltersBeforeHandleUrl={(filters) => ({
							...defaultTransformFiltersBeforeHandleUrl(filters),
							page: filters.page || 1,
						})}
					>
						<BlogInner
							lang={lang}
						/>
					</ItemsProvider>
				}
			/>
		</div>
	);
};

export default Blog;
